/* eslint-disable @typescript-eslint/no-floating-promises */
import { Membership } from '../../../../app/containers/Tournaments/types/Membership'
import { Tournament, TournamentWithMembership } from '../../../../app/containers/Tournaments/types/Tournament'
import { UserMembership } from '../../../../app/containers/Tournaments/types/UserMembership'
import { WebSocketEngine } from '../../../../app/containers/WebSocketProvider/engines/WebSocketEngine'
import { initializeSubscription } from '../../../../app/containers/WebSocketProvider/hooks/utils'
import { WebSocketInstance } from '../../../../app/containers/WebSocketProvider/types'
import {
  mergeTournamentsAndSubscriptions,
  markCurrentPlayer,
} from '../../../../app/utils/hooks/queries/tournaments/mergeTournamentsAndSubscriptions'
import player from '../../../User/Models/UserModel'
import router from '../../../router'
import { EVENT_NEW_DATA, EVENT_NEW_MEMBERSHIP_DATA, EVENT_TOURNAMENT_FOUND, LOAD_INTERVAL_TIME } from './constants'
import { RenderProps, RenderScoreBoardPops } from './types'
import {
  getGameTournaments,
  getGameVIPTournaments,
  getUserMemberships,
  getWebSocketAuthorizationData,
  subscribeTournament,
} from './utils'

class TournamentModel extends EventTarget {
  slug = ''
  tournament?: TournamentWithMembership
  userMemberships: UserMembership[] = []
  webSocketInstance?: WebSocketInstance
  timeoutId?: NodeJS.Timeout
  isSubscriptionPermitted = false
  isTournamentSubscribed = false
  isTournamentEnded = false
  isVipTournament = false
  playerLevel: string

  constructor() {
    super()
    this.slug = router.getRouterFromUrl(window.location.href).params.slug
    this.load()
    this.isSubscriptionPermitted = player.config.isRealPlayPermitted
    this.playerLevel = player.config.level
  }

  async load() {
    let tournaments: Tournament[] = []

    if (!this.isVipTournament) {
      tournaments = await getGameTournaments(this.slug)
    }

    if (tournaments.length === 0 && !!this.playerLevel) {
      tournaments = await getGameVIPTournaments(this.slug, this.playerLevel)
      this.isVipTournament = tournaments.length > 0
    }

    if (tournaments.length > 0) {
      if (this.isSubscriptionPermitted && !this.isTournamentSubscribed) {
        this.userMemberships = await getUserMemberships()
      }

      if (this.tournament === null) {
        this.dispatchEvent(new CustomEvent(EVENT_TOURNAMENT_FOUND))
      }

      this.render(tournaments)
      this.timeoutId = setTimeout(() => {
        this.load()
      }, LOAD_INTERVAL_TIME)
    }
  }

  stopRefetching() {
    clearTimeout(this.timeoutId)
  }

  async subscribe() {
    await subscribeTournament(this.tournament!.id)

    this.stopRefetching()
    this.load()
  }

  async render(tournament: Tournament[]) {
    const { tournaments, finishedTournaments } = mergeTournamentsAndSubscriptions(tournament, this.userMemberships)
    const tournamentWithSubscription = tournaments[0] ?? finishedTournaments[0]
    this.isTournamentEnded = tournaments.length === 0

    if (!this.isTournamentSubscribed) {
      this.isTournamentSubscribed = tournamentWithSubscription?.isSubscribed ?? false
    }

    this.tournament = tournamentWithSubscription
    await this.initializeScoreBoardUpdater(tournamentWithSubscription)

    this.dispatchEvent(
      new CustomEvent<RenderProps>(EVENT_NEW_DATA, {
        detail: {
          tournament: this.tournament,
          isSubscriptionPermitted: this.isSubscriptionPermitted && !this.isTournamentEnded,
        },
      }),
    )
  }

  updateScoreboard(memberships: Membership[]): void {
    const membershipsWithCurrentPlayer = markCurrentPlayer(memberships, this.userMemberships)
      .filter((user) => user.classified)
      .sort((userFirst, userPrevious) => (userFirst.position || 999999) - (userPrevious.position || 999999))

    this.dispatchEvent(
      new CustomEvent<RenderScoreBoardPops>(EVENT_NEW_MEMBERSHIP_DATA, {
        detail: { memberships: membershipsWithCurrentPlayer },
      }),
    )
  }

  async initializeScoreBoardUpdater(tournaments: TournamentWithMembership) {
    if (this.webSocketInstance) {
      return
    }

    this.webSocketInstance = await WebSocketEngine.getWebSocketEngine(getWebSocketAuthorizationData)
    initializeSubscription({
      webSocketInstance: this.webSocketInstance,
      channel: tournaments.channel,
      onPublication: (memberships: Membership[]) => this.updateScoreboard(memberships || []),
    })
  }
}

export default TournamentModel
